import { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'

import { useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import { firstOfMonth, lastOfMonth } from '@krp/ui-kit'
import {
  CashReceiptsScreen,
  ONEC_RECEIPT_WINDOWS,
  CashSalesStatisticsScreen,
  cashReceiptsArgsOf,
  cashBoundsArgsOf,
  cashReceiptsCashierChanged,
  cashReceiptsRefreshed,
  cashReceiptsKindChanged,
  cashReceiptsNodeChosen,
  cashReceiptsPeriodChanged,
  cashReceiptsSearchChanged,
  cashReceiptsTreeToggled,
  cashReceiptsViewOf,
  cashSummaryArgsOf,
  makeSelectCashReceiptsScreenVm,
  normalizeRid,
  onecCashBounds,
  onecCashReceipts,
  onecCashRegisters,
  reloadCashReceipts,
  type CashReceiptsScreenActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { localToday } from './supplyDates'
import { useChunkedSummary } from './useChunkedSummary'

/** Через сколько секунд после прошлого чтения список сегодняшнего дня перечитывается при входе (`refetchOnMountOrArgChange` в секундах). */
const TODAY_RECHECK_AFTER_S = 5

/**
 * Binding окна «Чеки 1С» (RP-FB12): rid — из окна (D-064). Кассы с деревом организаций и магазинов, чеки за период и
 * раскрытый чек — три чтения службы 1С; период по умолчанию — сегодня по часам человека. Кассы выбранного узла дерева
 * сначала должны быть известны: пока они не прочитаны, список чеков не спрашивается.
 */
function useCashReceiptsModel(statistics: boolean) {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const intent = useAppSelector((state) => state.onec.receipts)
  const view = cashReceiptsViewOf(intent, resourceId)
  const today = localToday()

  const fresh = view.refreshStamp
  // «Статистика по продажам» по «Обновить» перечитывает только сегодняшний день: кассы, границы базы и прошлые месяцы остаются из
  // кэша и не пропадают с экрана. Вкладка «Чеки» перечитывает всё, как раньше.
  const baseFresh = statistics ? 0 : fresh
  const baseView = statistics ? { ...view, refreshStamp: 0 } : view
  const registersArgs = useMemo(() => (resourceId === '' ? null : { resourceId, ...(baseFresh > 0 ? { fresh: baseFresh } : {}) }), [resourceId, baseFresh])
  const registers = useQueryResult(onecCashRegisters, registersArgs)
  const registerDirectory = registers.data?.data
  // «Статистика по продажам» чеков не показывает — и не читает их: только итоги периода, месяца и графики.
  const listArgs = useMemo(
    () => (statistics ? null : cashReceiptsArgsOf(resourceId, view, today, registerDirectory)),
    // Ключи касс узла зависят от справочника касс и выбранного узла, а не от остального отбора.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statistics, resourceId, view.nodeId, view.from, view.to, view.refreshStamp, today, registerDirectory],
  )
  // Чеки читаются только за один день; период из нескольких дней — статистика (служба сама читает 1С кусками по месяцу).
  const receipts = useQueryResult(onecCashReceipts, listArgs)
  // Сегодняшний день могли дополнить новыми чеками — его список перечитывается при каждом входе; прошлые дни берутся из кэша.
  onecCashReceipts.useQuerySubscription(listArgs ?? skipToken, { refetchOnMountOrArgChange: listArgs !== null && listArgs.from >= today ? TODAY_RECHECK_AFTER_S : false })
  // Порядок чтения: сначала то, что человек выбрал (по умолчанию — сегодняшний день), и только потом остальное — границы базы и
  // месяц графика. День считается прочитанным, когда его список пришёл или отказал; период из нескольких дней — когда пришла его статистика.
  const dayDone = listArgs === null || receipts.isSuccess || receipts.isError
  // Первый день с чеками в базе: месяцы до него не читаются. Короткий период его не ждёт, год и «за всё время» ждут (одна быстрая выборка).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const boundsArgs = useMemo(() => (dayDone ? cashBoundsArgsOf(resourceId, baseView) : null), [resourceId, baseFresh, dayDone])
  const bounds = useQueryResult(onecCashBounds, boundsArgs)
  const longPeriod = (view.to ?? today) > lastOfMonth(view.from ?? today)
  const boundsSettled = !longPeriod || boundsArgs === null || bounds.isSuccess || bounds.isError
  const firstDay = bounds.isSuccess ? (bounds.data?.data.first ?? null) : null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const summaryArgs = useMemo(() => (boundsSettled ? cashSummaryArgsOf(resourceId, view, today, firstDay) : null), [resourceId, view.from, view.to, view.refreshStamp, today, boundsSettled, firstDay])
  // Статистика выбранного периода читается первой — ровно его границы, а не весь месяц.
  const { result: rangeSummary, progress: rangeProgress, parts: rangeParts, retryFailed, todayRefreshing: rangeToday } = useChunkedSummary(summaryArgs)
  const rangeDone = summaryArgs === null || (rangeSummary !== null && (rangeSummary.isSuccess || rangeSummary.isError) && rangeProgress === null)
  const month = firstOfMonth(view.from ?? today)
  // Месяц для графика — фоном и строго после выбранного периода. Раз начатый, он остаётся включённым, пока открыт тот же месяц: смена дня
  // внутри месяца не отбирает у экрана уже прочитанное, а итоги дня берутся из кэша мгновенно.
  const monthKey = `${resourceId}|${month}|${baseFresh}`
  const [startedMonth, setStartedMonth] = useState('')
  // Месяц графика нужен только «Статистике по продажам»; во вкладке «Чеки» читается день и итоги выбранного периода.
  const monthReady = statistics && resourceId !== '' && dayDone && rangeDone
  if (monthReady && startedMonth !== monthKey) setStartedMonth(monthKey)
  const monthStarted = monthReady || startedMonth === monthKey
  const monthArgs = useMemo(
    () => (resourceId === '' || !monthStarted ? null : { resourceId, from: month, to: lastOfMonth(month), ...(fresh > 0 ? { fresh } : {}) }),
    [resourceId, month, fresh, monthStarted],
  )
  const useMonthSummary = resourceId !== '' && (view.from ?? today) >= month
    && (view.to ?? today) >= (view.from ?? today) && (view.to ?? today) <= lastOfMonth(month)
  const { result: monthSummary, todayRefreshing: monthToday } = useChunkedSummary(monthArgs)
  // Итоги — из месяца, когда он пришёл (те же числа, но без пересчёта при смене дня внутри месяца); пока его нет — из самого периода.
  const summary = useMonthSummary && monthSummary?.isSuccess === true ? monthSummary : rangeSummary
  const summaryProgress = rangeProgress

  const selectVm = useMemo(() => makeSelectCashReceiptsScreenVm(), [])
  const vm = selectVm({
    resourceId,
    today,
    intent,
    registers,
    receipts: listArgs === null ? null : receipts,
    summary,
    summaryProgress,
    monthSummary,
    bounds,
    summaryParts: rangeParts,
    todayRefreshing: rangeToday || monthToday,
  })

  const actions = useMemo<CashReceiptsScreenActions>(
    () => ({
      nodeChosen: (nodeId) => dispatch(cashReceiptsNodeChosen({ resourceId, nodeId })),
      nodeToggled: (nodeId) => dispatch(cashReceiptsTreeToggled({ resourceId, nodeId })),
      periodChanged: (range) => dispatch(cashReceiptsPeriodChanged({ resourceId, from: range.from, to: range.to })),
      kindChanged: (kind) => dispatch(cashReceiptsKindChanged({ resourceId, kind })),
      cashierChanged: (cashier) => dispatch(cashReceiptsCashierChanged({ resourceId, cashier })),
      searchChanged: (search) => dispatch(cashReceiptsSearchChanged({ resourceId, search })),
      openReceipt: (row) => openWindow(ONEC_RECEIPT_WINDOWS.receipt, { rid: resourceId, kind: row.kind, key: row.receiptKey }),
      refresh: () => dispatch(cashReceiptsRefreshed({ resourceId, stamp: Date.now() })),
      retryMonths: retryFailed,
      retry: () => dispatch(reloadCashReceipts(resourceId)),
    }),
    [dispatch, openWindow, resourceId, retryFailed],
  )

  return { vm, actions }
}

export function CashReceiptsBinding() {
  const { vm, actions } = useCashReceiptsModel(false)
  return <CashReceiptsScreen vm={vm} actions={actions} />
}

/** Окно «Статистика по продажам 1С»: те же чтения и тот же vm, плюс месяц графика и сводки. */
export function CashSalesStatisticsBinding() {
  const { vm, actions } = useCashReceiptsModel(true)
  return <CashSalesStatisticsScreen vm={vm} actions={actions} />
}
